import React, { useCallback, useMemo } from 'react';
import { GoogleMap } from 'modules/shared/features/google-map';
import { TMapCamera } from '@api/types/camera-map.type';

interface MapCameraOverlayProps {
    camera: TMapCamera;
    cameraAngle: number;
    cameraRange: number;
    cameraFOV: number;

    isEditingLocation?: boolean;
    tempMarkerPosition?: google.maps.LatLngLiteral | null;
    onMarkerDragEnd?: (position: google.maps.LatLngLiteral) => void;
}

const MapCameraOverlay = (props: MapCameraOverlayProps) => {
    const { camera, cameraAngle, cameraRange, cameraFOV, isEditingLocation, tempMarkerPosition, onMarkerDragEnd } =
        props;

    // const cameraPosition = useMemo(
    //     () => ({
    //         lat: +camera.location.latitude,
    //         lng: +camera.location.longitude,
    //     }),
    //     [camera.location.latitude, camera.location.longitude]
    // );

    const lat = Number(camera.location.latitude);
    const lng = Number(camera.location.longitude);

    const hasValidCoords = Number.isFinite(lat) && Number.isFinite(lng);
    if (!hasValidCoords) return null;

    const cameraPosition = { lat, lng };
    const { position: contentPosition, direction } = useMemo(() => {
        const normalized = (n: number) => ((n % 360) + 360) % 360;

        let tooltipAngle = normalized(cameraAngle + 180);
        if (cameraFOV >= 150) tooltipAngle = normalized(cameraAngle + 90);

        const offsetPx = cameraFOV >= 120 ? 16 : 8;

        let pos: React.CSSProperties;
        let dir: 'top' | 'bottom' | 'left' | 'right';

        if (tooltipAngle >= 315 || tooltipAngle < 45) {
            pos = { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: `${offsetPx}px` };
            dir = 'top';
        } else if (tooltipAngle >= 45 && tooltipAngle < 135) {
            pos = { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: `${offsetPx}px` };
            dir = 'right';
        } else if (tooltipAngle >= 135 && tooltipAngle < 225) {
            pos = { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: `${offsetPx}px` };
            dir = 'bottom';
        } else {
            pos = { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: `${offsetPx}px` };
            dir = 'left';
        }

        return { position: pos, direction: dir };
    }, [cameraAngle, cameraFOV]);

    const arrowConfig = useMemo(() => {
        return {
            position: {
                bottom: { top: '-14px', left: '50%', transform: 'translateX(-50%)' },
                top: { bottom: '-14px', left: '50%', transform: 'translateX(-50%)' },
                left: { right: '-14px', top: '50%', transform: 'translateY(-50%)' },
                right: { left: '-14px', top: '50%', transform: 'translateY(-50%)' },
            }[direction],
            rotation: { bottom: 180, left: 270, top: 0, right: 90 }[direction],
        };
    }, [direction]);

    const renderIcon = useCallback(() => {
        if (camera.latestImage?.urlThumb) {
            return (
                <img
                    className="w-10 h-10 rounded-md object-cover object-center"
                    src={camera.latestImage.urlThumb}
                    alt=""
                />
            );
        }

        // fallback
        return (
            <div className="w-10 h-10 bg-grayscale-100 rounded-md flex items-center justify-center">
                <div className="text-xs text-grayscale-500">IMG</div>
            </div>
        );
    }, [camera.latestImage?.urlThumb]);

    return (
        <div className="w-full h-full relative">
            {/* Normal marker + tooltip */}
            {!isEditingLocation && (
                <GoogleMap.AdvancedMarker position={cameraPosition} title="Camera">
                    <div className="relative flex justify-center">
                        <div className="absolute flex flex-col items-center drop-shadow-md" style={contentPosition}>
                            <div className="bg-card-color fill-card-color px-3 py-3 rounded-lg text-sm">
                                <div className="absolute" style={arrowConfig.position}>
                                    <svg
                                        className="fill-inherit"
                                        fill="currentColor"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        style={{
                                            transform: `rotate(${arrowConfig.rotation}deg)`,
                                            transformOrigin: 'center',
                                        }}
                                    >
                                        <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z" />
                                    </svg>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div>{renderIcon()}</div>
                                    <div>
                                        <div className="font-medium whitespace-nowrap">{camera.name}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </GoogleMap.AdvancedMarker>
            )}

            {/* Draggable marker while editing */}
            {isEditingLocation && tempMarkerPosition && (
                <GoogleMap.AdvancedMarker
                    position={tempMarkerPosition}
                    title="Click to change location"
                    draggable
                    onMarkerDragEnd={onMarkerDragEnd}
                    zIndex={20}
                >
                    <div className="relative">
                        <div className="w-8 h-8 bg-primary rounded-full border-4 border-white shadow-lg flex items-center justify-center animate-pulse">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-6 bg-red-500" />
                    </div>
                </GoogleMap.AdvancedMarker>
            )}

            {/* FOV polygon */}
            {!isEditingLocation && !tempMarkerPosition && (
                <GoogleMap.Polygon
                    center={cameraPosition}
                    angle={cameraAngle}
                    range={cameraRange}
                    fieldOfView={cameraFOV}
                    fillColor="#4285F4"
                    strokeColor="#1967D2"
                    fillOpacity={0.35}
                    strokeOpacity={0.8}
                    strokeWeight={2}
                />
            )}
        </div>
    );
};

export default MapCameraOverlay;
